/**
 * @fileoverview Shared "Scheduled" badge for report/dashboard grid rows — replaces
 *             the per-page inline schedule indicators (report-template refactor).
 * @module ui-admin/components/Badges/ScheduleBadge
 *
 * Responsibilities: - Render a "Scheduled · <cadence>" chip when the report has
 *                     ≥1 active schedule; render an em-dash when it has none.
 *                   - Tooltip lists the active schedule names + next run dates.
 * Dependencies:     - React, lucide-react (CalendarClock), ./Badges (Badge core).
 * Architecture:     - Presentation-only. The host derives scheduleCount/cadenceLabel
 *                     from its local-first schedule service (useReportSchedules).
 *                     No Dexie, no API, no mock data.
 * Notes:            - Co-located with the datagrid-cleanup badges; the dashboard
 *                     refactor adds a sibling AssignmentBadge to this directory.
 */

import React from 'react';
import { CalendarClock } from 'lucide-react';
import { Badge } from './Badges';

/**
 * @contract ScheduleBadgeProps
 * Purpose: Props for the ScheduleBadge cell renderer.
 * Fields:
 * - scheduleCount: number of active schedules bound to the report row.
 * - cadenceLabel: human-readable cadence of the primary active schedule
 *   (e.g. "Monthly", "Weekly"); derived by the host from the schedule preset.
 * - scheduleSummaries: optional lines for the title tooltip
 *   ("<name> · next <ISO date>").
 */
export interface ScheduleBadgeProps {
  scheduleCount: number;
  cadenceLabel?: string;
  scheduleSummaries?: string[];
}

/**
 * @component ScheduleBadge
 * Purpose:     "Scheduled" chip with cadence for a report grid row.
 * Props:       ScheduleBadgeProps.
 * Behavior:    scheduleCount > 0 → "Scheduled · <cadenceLabel>" (info tone,
 *              calendar icon). 0 → "—" neutral text (no chip).
 * Data Source: local (props from host).
 * Constraints: presentation-only; no Dexie, no API calls.
 */
export function ScheduleBadge({ scheduleCount, cadenceLabel, scheduleSummaries }: ScheduleBadgeProps) {
  if (scheduleCount <= 0) {
    return <span className="text-xs text-gray-400" data-testid="schedule-badge-none">—</span>;
  }
  const label = cadenceLabel ? `Scheduled · ${cadenceLabel}` : `Scheduled · ${scheduleCount}`;
  const tooltip = scheduleSummaries && scheduleSummaries.length > 0 ? scheduleSummaries.join('\n') : undefined;
  return (
    <span title={tooltip} data-testid="schedule-badge">
      <Badge label={label} variant="info" icon={CalendarClock} />
    </span>
  );
}
